import React, { useState, useEffect } from 'react';
import { ticketCategoryService } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import './Categories.css';

const toList = (data) => (Array.isArray(data) ? data : (data?.results ?? []));

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    display_order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await ticketCategoryService.getAll();
      setCategories(toList(res.data));
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await ticketCategoryService.update(editingCategory.id, formData);
      } else {
        await ticketCategoryService.create(formData);
      }
      setShowModal(false);
      setEditingCategory(null);
      resetForm();
      fetchCategories();
    } catch (error) {
      const msg = error.response?.data?.code?.[0] || error.response?.data?.detail || 'Error saving category';
      alert(msg);
    }
  };

  const handleEdit = (cat) => {
    setEditingCategory(cat);
    setFormData({
      name: cat.name,
      code: cat.code,
      display_order: cat.display_order ?? 0,
      is_active: cat.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await ticketCategoryService.delete(id);
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting category');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', code: '', display_order: 0, is_active: true });
  };

  if (loading) return <div className="loading">Loading categories...</div>;

  return (
    <div className="categories-page">
      <div className="page-header">
        <h1>Categories</h1>
        <button className="btn-primary" onClick={() => { setShowModal(true); setEditingCategory(null); resetForm(); }}>
          <FiPlus />
          Add Category
        </button>
      </div>

      <div className="categories-list">
        {categories.map((cat) => (
          <div key={cat.id} className="category-card">
            <div className="category-info">
              <h3>{cat.name}</h3>
              <p className="category-code">Code: {cat.code}</p>
              <span className={`badge ${cat.is_active ? 'active' : 'inactive'}`}>
                {cat.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="category-actions">
              <button type="button" className="btn-edit" onClick={() => handleEdit(cat)}><FiEdit2 /></button>
              <button type="button" className="btn-delete" onClick={() => handleDelete(cat.id)}><FiTrash2 /></button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingCategory(null); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="e.g. adult, child"
                  required
                  readOnly={!!editingCategory}
                />
                {editingCategory && <small className="form-hint">Code cannot be changed when editing.</small>}
              </div>
              <div className="form-group">
                <label>Display order</label>
                <input
                  type="number"
                  min="0"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingCategory(null); resetForm(); }}>Cancel</button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
